import { LorawanDevicesPage } from "./pages/LorawanDevicesPage";
import { useEffect } from "react";
import { Navigate, Route, Routes } from "react-router-dom";
import { useAuth } from "./auth";
import { api, type Farm } from "./api";
import { Layout } from "./pages/Layout";
import { Login } from "./pages/Login";
import { AdminLogin } from "./pages/AdminLogin";
import { FarmShell } from "./pages/FarmShell";
import { SettingsShell } from "./pages/Settings";
import { FarmsPage } from "./pages/Farms";
import { UsersPage } from "./pages/Users";
import { ProfilePage } from "./pages/Profile";
import { OwnersAdminPage } from "./pages/OwnersAdmin";

const LAST_FARM_KEY = "cloud_last_farm_id";

/** Gate: redirect to /login when unauthenticated. Wait for the boot-time
 *  /auth/me so a valid session doesn't flash to login. */
function Private({ children }: { children: JSX.Element }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="login-shell">Đang tải…</div>;
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function AdminOnly({ children }: { children: JSX.Element }) {
  const { user } = useAuth();
  if (!user?.is_platform_admin) return <Navigate to="/" replace />;
  return children;
}

/** Landing redirect: users go straight into Farm Control for the last-
 *  selected farm (persisted in localStorage) or the first owned farm.
 *
 *  Degenerate cases:
 *    • Platform admin with no tenant farms — /admin/owners.
 *    • Owner with no farms yet — /settings/farms to create one.
 */
function HomeRedirect() {
  const { user } = useAuth();

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const farms = await api.get<Farm[]>("/farms");
        if (cancelled) return;
        const remembered = localStorage.getItem(LAST_FARM_KEY);
        const pick =
          (remembered && farms.find((f) => f.id === remembered)?.id) ||
          farms[0]?.id ||
          null;
        if (pick) {
          localStorage.setItem(LAST_FARM_KEY, pick);
          // react-router navigate, not window.location — FarmShell is a
          // cloud-portal route, same SPA.
          window.location.replace(`/farms/${pick}/`);
        } else {
          window.location.replace(
            user?.is_platform_admin ? "/admin/owners" : "/settings/farms",
          );
        }
      } catch {
        if (!cancelled) window.location.replace("/login");
      }
    })();
    return () => { cancelled = true; };
  }, [user]);

  return <div className="login-shell">Đang mở Farm Control…</div>;
}

export default function App() {
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/admin/login" element={<AdminLogin />} />

      {/* Landing → farm shell redirect */}
      <Route path="/" element={<Private><HomeRedirect /></Private>} />

      {/* Farm Control — standalone full-height shell with top bar
          (farm switcher + user menu) above an iframe to /app/?farm=<id>.
          NOT wrapped in Layout — it has its own chrome. */}
      <Route path="/farms/:farmId/*" element={<Private><FarmShell /></Private>} />

      {/* Settings + admin use the sidebar Layout shell. */}
      <Route element={<Private><Layout /></Private>}>
        <Route path="/settings" element={<SettingsShell />}>
          <Route index element={<Navigate to="farms" replace />} />
          <Route path="farms" element={<FarmsPage />} />
          <Route path="users" element={<UsersPage />} />
          <Route path="profile" element={<ProfilePage />} />
          <Route path="lorawan" element={<LorawanDevicesPage />} />
        </Route>

        <Route path="/admin" element={<AdminOnly><Navigate to="/admin/owners" replace /></AdminOnly>} />
        <Route path="/admin/owners" element={<AdminOnly><OwnersAdminPage /></AdminOnly>} />
      </Route>

      {/* Legacy standalone redirects — old bookmarks to /farms, /users, /masters */}
      <Route path="/farms" element={<Navigate to="/settings/farms" replace />} />
      <Route path="/users" element={<Navigate to="/settings/users" replace />} />
      <Route path="/masters" element={<Navigate to="/settings/farms" replace />} />

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
